import { useState } from 'react';
import { navigate } from '../../app/navigation';
import type { V2Area, V2ChecklistItem } from '../../domain/inspection';
import { useT, type MessageKey } from '../../i18n/i18n';
import { areaDisplayName, areaKindLabel, floorLabelFor, itemTitleFor } from '../../i18n/labels';
import type { AiUncertainReason, PreDepartureCheck, PreDepartureGroup, PreDepartureLine, PreDepartureLevel } from '../../state/preDeparture';
import type { IconName } from '../../ui/icons';
import { ListRow, SectionHeader } from '../../ui/components';

// 离开前再看一眼 · Before you leave (2026-10-27, src/state/preDeparture.ts): the advisory list of what is
// easiest to settle while the inspector is still in the house — rows without a photo, what the AI could
// not settle, a level whose smoke / CO alarm was never answered. Grouped by area; an area with more than
// COLLAPSE_AFTER lines shows that many and the rest behind 「再显示 n 项」. Every line opens its row (or the
// area, for an area-level line) exactly like the blocking gaps' 去补. Never blocks the report.
const COLLAPSE_AFTER = 3;

const REASON_KEY: Record<AiUncertainReason, MessageKey> = {
  fieldTest: 'closeout.leaveAiFieldTest',
  unclear: 'closeout.leaveAiUnclear',
  lowDefect: 'closeout.leaveAiLowDefect',
  lowNoDefect: 'closeout.leaveAiLow'
};

const ICON: Record<PreDepartureLine['kind'], IconName> = {
  aiUncertain: 'psychology',
  aiQuestion: 'question_mark',
  aiUnfiled: 'category',
  aiNewPhotos: 'photo_library',
  alarmLevel: 'health_and_safety',
  noPhoto: 'add_a_photo'
};

export function BeforeLeaveSection(props: { inspectionId: string; check: PreDepartureCheck; areas: readonly V2Area[]; items: readonly V2ChecklistItem[] }) {
  const { t, tp } = useT();
  if (props.check.total === 0) return null;
  const areaById = new Map(props.areas.map((a) => [a.id, a] as const));
  const itemById = new Map(props.items.map((i) => [i.id, i] as const));
  return (
    <div className="list" data-testid="closeout-before-leave" data-total={props.check.total}>
      <SectionHeader title={t('closeout.leaveTitle')} meta={tp('closeout.leaveMeta', props.check.total)} />
      <p className="card-note closeout-leave-body">{t('closeout.leaveBody')}</p>
      {props.check.groups.map((group) => (
        <BeforeLeaveGroup key={group.areaId} inspectionId={props.inspectionId} group={group} area={areaById.get(group.areaId) ?? null} itemById={itemById} />
      ))}
    </div>
  );
}

function BeforeLeaveGroup(props: { inspectionId: string; group: PreDepartureGroup; area: V2Area | null; itemById: ReadonlyMap<string, V2ChecklistItem> }) {
  const { t, tp, locale } = useT();
  const [expanded, setExpanded] = useState(false);
  const lines = props.group.lines;
  const hidden = lines.length > COLLAPSE_AFTER && !expanded ? lines.length - COLLAPSE_AFTER : 0;
  const shown = hidden > 0 ? lines.slice(0, COLLAPSE_AFTER) : lines;
  const areaName = props.area ? areaDisplayName(locale, props.area) : '';

  function levelLabel(level: PreDepartureLevel | null): string {
    if (!level) return '';
    return level.kind === 'floor' ? floorLabelFor(t, level.floor) : areaKindLabel(t, 'basement');
  }

  function open(entry: PreDepartureLine) {
    navigate({ screen: 'areaWorkspace', inspectionId: props.inspectionId, areaId: entry.areaId, itemSheetId: entry.itemId });
  }

  function texts(entry: PreDepartureLine): { title: string; subtitle: string } {
    const item = entry.itemId ? (props.itemById.get(entry.itemId) ?? null) : null;
    const itemTitle = item ? itemTitleFor(locale, item) : '';
    switch (entry.kind) {
      case 'aiUncertain': {
        const reason = entry.reason ? t(REASON_KEY[entry.reason]) : '';
        const state = entry.rowOpen ? t('closeout.leaveRowOpen') : entry.reason === 'fieldTest' ? t('closeout.leaveRowNoTest') : t('closeout.leaveRowPass');
        return { title: itemTitle, subtitle: entry.question ? `${t('closeout.leaveAiAsks', { text: entry.question })} · ${state}` : `${reason} · ${state}` };
      }
      case 'aiQuestion':
        return {
          title: itemTitle || entry.title || t('closeout.leaveAiQuestionTitle'),
          subtitle: entry.source === 'advanced' ? t('closeout.leaveAdvancedAsks', { text: entry.question ?? '' }) : t('closeout.leaveAiAsks', { text: entry.question ?? '' })
        };
      case 'aiUnfiled':
        return { title: tp('closeout.leaveUnfiled', entry.count), subtitle: t('closeout.leaveUnfiledBody') };
      case 'aiNewPhotos':
        return { title: tp('closeout.leaveNewPhotos', entry.count), subtitle: t('closeout.leaveNewPhotosBody') };
      case 'alarmLevel':
        return { title: t('closeout.leaveAlarmTitle', { level: levelLabel(entry.level) }), subtitle: t('closeout.leaveAlarmBody') };
      case 'noPhoto':
        return { title: itemTitle, subtitle: entry.defect ? t('closeout.leaveNoPhotoDefect') : t('closeout.leaveNoPhoto') };
    }
  }

  return (
    <div className="closeout-leave-group" data-testid="closeout-leave-group" data-area-id={props.group.areaId}>
      <div className="group-head">
        <span className="group-dot" aria-hidden="true" />
        <h3 className="group-title">{areaName}</h3>
        <span className="group-count">{lines.length}</span>
        {lines.length > COLLAPSE_AFTER ? (
          <button type="button" className="link-inline group-action closeout-leave-toggle" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded} data-testid="closeout-leave-toggle">
            {expanded ? t('common.collapse') : t('closeout.leaveShowAll', { n: lines.length - COLLAPSE_AFTER })}
          </button>
        ) : null}
      </div>
      {shown.map((entry, index) => {
        const { title, subtitle } = texts(entry);
        return (
          <ListRow
            key={`${entry.kind}-${entry.itemId ?? ''}-${index}`}
            icon={ICON[entry.kind]}
            iconTone="soft"
            title={title}
            subtitle={subtitle}
            trailing={<span className="badge badge-muted">{t('closeout.fix')}</span>}
            chevron
            onClick={() => open(entry)}
            dataName={title}
            testId="closeout-leave-row"
          />
        );
      })}
    </div>
  );
}
